// src/data/topUpProductMap.ts
// ---------------------------
// Repository for `top_up_product_map` (db-158) — the editable Stock-Top-up section →
// Unleashed product code + SO suffix mapping. The Phase-4 SO builder reads it; the
// Top-up Product Mapping admin screen edits it. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export interface TopUpProductMapRow {
  id: string;
  section: string;
  item_key: string;
  product_code: string;
  sort_order: number;
  deactivated: boolean;
}

// Typed as `string` so supabase-js doesn't deep-parse the select. `suffix` moved to
// top_up_so_rules (db-160) — the mapping owns only the Unleashed product code.
const TOP_UP_MAP_COLS: string = "id, section, item_key, product_code, sort_order, deactivated";

// The full mapping, sort_order-ordered — active only by default (what the builder uses),
// pass includeDeactivated for the admin view. [] on error / no client.
export async function listTopUpProductMap(
  includeDeactivated = false
): Promise<TopUpProductMapRow[]> {
  const c = getTypedClient();
  if (!c) return [];
  let q = c.from("top_up_product_map").select(TOP_UP_MAP_COLS);
  if (!includeDeactivated) q = q.eq("deactivated", false);
  const { data, error } = await q.order("sort_order").limit(200);
  if (error) {
    logger.warn("listTopUpProductMap failed:", error.message);
    return [];
  }
  return (data ?? []) as unknown as TopUpProductMapRow[];
}

type TopUpMapPatch = Database["public"]["Tables"]["top_up_product_map"]["Update"];
type WriteResult = { error: { message: string } | null };

// Update a mapping row by id (the admin editor edits product_code; the suffix moved to
// top_up_so_rules in db-160). Takes a loose patch cast to the generated Update shape.
export async function updateTopUpProductMapById(
  id: string | undefined,
  patch: object
): Promise<WriteResult> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("top_up_product_map")
    .update(patch as TopUpMapPatch)
    .eq("id", id ?? "");
  if (error) logger.warn("updateTopUpProductMapById failed:", error.message);
  return { error };
}
